import { useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import { isString, isEqual } from 'lodash'
import {
    useFetchNotConfidentSubmissionsQuery,
    useFetchSubmissionReviewsQuery,
    useFilterByCriteriaQuery,
} from '../../../../store/apis/submission.api'
import useQueryParam from '../../../../hooks/useQueryParam'
import {
    SORT_PARAM,
    STUDENT_FEEDBACK_EXACT_MATCH_PARAM,
    STUDENT_FEEDBACK_PARAM,
    SUBMISSION_SEARCH_PARAM,
} from '../../../../constants/query-params'
import { DEFAULT_CLUSTER_SORTING_TYPE } from '../../../../constants/sorting-types'
import { CLUSTER_X_ID, CLUSTER_Y_ID } from '../../../../constants/cluster-ids'
import { REVIEW_FILTERS } from '../../sections/clusters/filters/ReviewFilters'
import { sortClusters, sortClustersFiles } from '../../../../utils/cluster.helper'
import patternSelectors from '../../../../store/selectors/pattern.selectors'
import { submissionActions } from '../../../../store/slices/submission.slice'
import { PATTERN_TYPES } from '../../../../constants/pattern-types'
import { openNotificationWithIcon } from '../../../../utils/notification.helper'
import { LANGUAGES } from '../../../../constants/languages'

const useClusterGroups = ({ applyFilter = true } = {}) => {
    const dispatch = useDispatch()
    const { token, layerId } = useParams()

    // Redux selectors
    const assignment = useSelector(state => state.assignment.assignment)
    const rawClusters = useSelector(state => state.assignment.clusters)
    const filters = useSelector(state => state.assignment.filters)
    const unitTestResults = useSelector(state => state.assignment.unitTestResults)
    const finetunedPatternExists = useSelector(patternSelectors.finetunedPatternExists)

    // Query params
    const searchParam = useQueryParam(SUBMISSION_SEARCH_PARAM)
    const sortParam = useQueryParam(SORT_PARAM) || DEFAULT_CLUSTER_SORTING_TYPE
    const studentFeedbackParam = useQueryParam(STUDENT_FEEDBACK_PARAM)
    const studentFeedbackExactMatchParam = useQueryParam(STUDENT_FEEDBACK_EXACT_MATCH_PARAM)

    // RTK Query hooks
    const { data: submissionReviews } = useFetchSubmissionReviewsQuery({ token, layerId })
    const { data: notConfidentSubmissions } = useFetchNotConfidentSubmissionsQuery(
        { token, layerId },
        { skip: !finetunedPatternExists },
    )

    // Local state for criteria filtering
    const [criteriaFilteredFileIds, setCriteriaFilteredFileIds] = useState([])

    // Determine if we have criteria to filter
    const hasCriteriaToFilter = useMemo(
        () =>
            filters.patterns.some(
                pattern =>
                    pattern.type === PATTERN_TYPES.SEMANTIC.value &&
                    pattern.criteria &&
                    pattern.criteria.some(criterion => criterion.enabled),
            ),
        [filters.patterns],
    )

    // Build criteria conditions for the query
    const criteriaConditions = useMemo(() => {
        if (!hasCriteriaToFilter) return []

        const conditions = []
        filters.patterns.forEach(pattern => {
            if (pattern.type === PATTERN_TYPES.SEMANTIC.value) {
                pattern.criteria.forEach((criterion, index) => {
                    if (criterion.enabled) {
                        conditions.push(`${pattern.id},${index},${criterion.show}`)
                    }
                })
            }
        })

        return conditions
    }, [filters.patterns])

    // Use the RTK Query hook with skip option when no criteria are enabled
    const { data: filteredFileIds, isError } = useFilterByCriteriaQuery(
        { token, layerId, criteriaConditions, exactMatch: filters.exactMatch },
        { skip: criteriaConditions.length === 0 },
    )

    // Update the local state when the query results change
    useEffect(() => {
        if (isError) {
            console.error('Error applying criteria filtering')
            setCriteriaFilteredFileIds([])
        } else if (filteredFileIds !== undefined) {
            setCriteriaFilteredFileIds(filteredFileIds)
        } else if (!hasCriteriaToFilter) {
            setCriteriaFilteredFileIds([])
        }
    }, [filteredFileIds, isError, hasCriteriaToFilter])

    // Memoized filters object
    const clusterFilters = useMemo(
        () => ({
            searchParam,
            sortParam,
            studentFeedbackParam,
            studentFeedbackExactMatchParam,
            filters,
            unitTestResults,
            submissionReviews,
            criteriaFilteredFileIds,
            hasCriteriaToFilter,
        }),
        [
            searchParam,
            sortParam,
            studentFeedbackParam,
            studentFeedbackExactMatchParam,
            filters,
            unitTestResults,
            submissionReviews,
            criteriaFilteredFileIds,
            hasCriteriaToFilter,
        ],
    )

    // Use deferred values to prevent excessive re-renders during rapid changes
    const deferredFilters = useDeferredValue(clusterFilters)

    // Student feedback processing
    const processStudentFeedback = useCallback((studentFeedbackParam, studentFeedbackExactMatchParam) => {
        const studentFeedbackCheckedValues = studentFeedbackParam ? studentFeedbackParam.split(',') : []
        const studentFeedbackExactMatch = studentFeedbackExactMatchParam === 'true'
        return { studentFeedbackCheckedValues, studentFeedbackExactMatch }
    }, [])

    // File filtering functions
    const checkStudentFeedbackFilter = useCallback((file, studentFeedbackCheckedValues, studentFeedbackExactMatch) => {
        if (studentFeedbackCheckedValues.length === 0) return true

        const fileStudentFeedbackFlags = {
            liked: file.student_feedback?.liked,
            disliked: file.student_feedback ? !file.student_feedback.liked : false,
            commented: Boolean(file.student_feedback?.comment?.trim()),
        }

        if (studentFeedbackExactMatch) {
            return studentFeedbackCheckedValues.every(v => fileStudentFeedbackFlags[v] === true)
        } else {
            return studentFeedbackCheckedValues.some(v => fileStudentFeedbackFlags[v] === true)
        }
    }, [])

    const checkPatternFilters = useCallback((file, filters) => {
        const enabledPatternFilterIds = filters.patterns.reduce((result, pattern) => {
            if (pattern.enabled && pattern.show) result.push(pattern.id)
            return result
        }, [])

        const checkIfFileHasPattern = id => file.motifList.some(motif => Number(motif.motif_id) === Number(id))

        const fileMatchesPatternsThatShouldBeShown = filters.exactMatch
            ? enabledPatternFilterIds.every(checkIfFileHasPattern)
            : enabledPatternFilterIds.length
              ? enabledPatternFilterIds.some(checkIfFileHasPattern)
              : true

        const patternFilterIdsThatShouldBeIgnored = filters.patterns.reduce((result, pattern) => {
            if (pattern.enabled && !pattern.show) result.push(pattern.id)
            return result
        }, [])

        const fileMatchesPatternsThatShouldBeIgnored = filters.exactMatch
            ? patternFilterIdsThatShouldBeIgnored.some(checkIfFileHasPattern)
            : patternFilterIdsThatShouldBeIgnored.every(checkIfFileHasPattern)

        let fileMatchesAllPatternFilters = true
        if (enabledPatternFilterIds.length && patternFilterIdsThatShouldBeIgnored.length) {
            fileMatchesAllPatternFilters = filters.exactMatch
                ? fileMatchesPatternsThatShouldBeShown && !fileMatchesPatternsThatShouldBeIgnored
                : fileMatchesPatternsThatShouldBeShown || !fileMatchesPatternsThatShouldBeIgnored
        } else if (enabledPatternFilterIds.length) {
            fileMatchesAllPatternFilters = fileMatchesPatternsThatShouldBeShown
        } else if (patternFilterIdsThatShouldBeIgnored.length) {
            fileMatchesAllPatternFilters = !fileMatchesPatternsThatShouldBeIgnored
        }

        return fileMatchesAllPatternFilters
    }, [])

    const checkMissingPatternFeedback = useCallback((file, filters, assignment) => {
        return filters.feedback.missingPatternFeedback
            ? file.motifList.some(motif => {
                  const motifQuery = assignment.patterns.find(motifQuery => {
                      return Number(motif.motif_id) === Number(motifQuery.motif_id)
                  })
                  return !motifQuery.feedback
              })
            : true
    }, [])

    const checkUnitTestFilters = useCallback((file, filters, unitTestResults) => {
        return filters.unitTests.every(unitTest => {
            if (!unitTest.enabled) {
                return true
            }
            try {
                return (
                    unitTest.passed ===
                    Boolean(
                        unitTestResults.find(
                            unitTestResult =>
                                unitTestResult.file_id === file.id && unitTestResult.ut_id === unitTest.id,
                        )?.ut_passed,
                    )
                )
            } catch (e) {
                console.log('Results for that file were not found: ' + e)
                return false
            }
        })
    }, [])

    const checkSearchParam = useCallback((file, searchParam, assignment, fileIdsToSelect) => {
        if (!searchParam) return true

        if (searchParam.startsWith('/') && searchParam.endsWith('/')) {
            try {
                if (new RegExp(searchParam.slice(1, -1)).test(assignment.files[file.id]?.content)) {
                    fileIdsToSelect.push(file.id)
                }
                return true
            } catch {
                openNotificationWithIcon('warning', 'The regex is invalid')
                return true
            }
        } else {
            return assignment.files && searchParam
                ? file.name?.toLowerCase().includes(searchParam.toLowerCase()) ||
                      (isString(assignment.files[file.id]?.content) &&
                          assignment.files[file.id].content.toLowerCase().includes(searchParam.toLowerCase()))
                : true
        }
    }, [])

    const checkFlakyFilter = useCallback((file, filters, assignment) => {
        return filters.properties.isFlaky
            ? file.motifList.some(motif =>
                  assignment.patterns.some(pattern => motif.motif_id === pattern.motif_id && pattern.is_flaky),
              )
            : true
    }, [])

    const checkReviewFilter = useCallback((file, filters, submissionReviews) => {
        if (filters.review === REVIEW_FILTERS.UNREVIEWED) {
            return !submissionReviews?.some(review => review.submissionId === file.id)
        } else if (filters.review === REVIEW_FILTERS.REVIEWED) {
            return submissionReviews?.some(review => review.submissionId === file.id)
        }
        return true
    }, [])

    // Main cluster processing function
    const processCluster = useCallback(
        (
            rawClusters,
            {
                searchParam,
                sortParam,
                studentFeedbackParam,
                studentFeedbackExactMatchParam,
                filters,
                unitTestResults,
                submissionReviews,
                criteriaFilteredFileIds,
                hasCriteriaToFilter,
            },
        ) => {
            if (!rawClusters || !assignment) return { clusters: [], fileIdsToSelect: [] }

            let _clusters = rawClusters.map(cluster => ({ ...cluster }))
            const fileIdsToSelect = []

            const { studentFeedbackCheckedValues, studentFeedbackExactMatch } = processStudentFeedback(
                studentFeedbackParam,
                studentFeedbackExactMatchParam,
            )

            const shouldApplyFiltering = () =>
                searchParam ||
                filters.patterns.some(pattern => pattern.enabled) ||
                filters.feedback.missingPatternFeedback ||
                filters.feedback.missingGroupFeedback ||
                Object.values(filters.properties).some(property => property) ||
                filters.unitTests.some(unitTest => unitTest.enabled) ||
                filters.review !== REVIEW_FILTERS.ALL ||
                studentFeedbackCheckedValues.length > 0 ||
                hasCriteriaToFilter

            if (shouldApplyFiltering()) {
                _clusters = _clusters.filter(cluster => {
                    const updatedClusterFiles = cluster?.files?.filter(file => {
                        // Criteria filtering check
                        if (
                            hasCriteriaToFilter &&
                            !criteriaFilteredFileIds.includes(file.id)
                        ) {
                            return false
                        }

                        // Apply all filter checks
                        return (
                            checkStudentFeedbackFilter(file, studentFeedbackCheckedValues, studentFeedbackExactMatch) &&
                            checkPatternFilters(file, filters) &&
                            checkMissingPatternFeedback(file, filters, assignment) &&
                            checkUnitTestFilters(file, filters, unitTestResults) &&
                            checkSearchParam(file, searchParam, assignment, fileIdsToSelect) &&
                            checkFlakyFilter(file, filters, assignment) &&
                            checkReviewFilter(file, filters, submissionReviews)
                        )
                    })

                    if (updatedClusterFiles) {
                        cluster.files = updatedClusterFiles
                    }

                    return filters.feedback.missingGroupFeedback ? !cluster.feedback : true
                })

                // Dispatch selected file IDs
                dispatch(submissionActions.setSelectedSubmissionIds(fileIdsToSelect))
            }

            // Filter out undefined/null clusters
            _clusters = _clusters.filter(cluster => cluster !== undefined && cluster !== null)

            // Sort clusters and files
            _clusters = sortClusters(_clusters, sortParam)
            _clusters = sortClustersFiles(_clusters, notConfidentSubmissions)

            // Handle special clusters X and Y
            const indexOfClusterX = _clusters.findIndex(cluster => cluster?.cluster_id === CLUSTER_X_ID)
            const clusterX = indexOfClusterX !== -1 ? _clusters.splice(indexOfClusterX, 1)[0] : null

            const indexOfClusterY = _clusters.findIndex(cluster => cluster?.cluster_id === CLUSTER_Y_ID)
            let clusterY = indexOfClusterY !== -1 ? _clusters.splice(indexOfClusterY, 1)[0] : null

            if (assignment.language == LANGUAGES.TEXT.value) {
                clusterY = undefined
            }

            const finalClusters = [..._clusters, ...(clusterX ? [clusterX] : []), ...(clusterY ? [clusterY] : [])]

            return { clusters: finalClusters, fileIdsToSelect }
        },
        [
            assignment,
            dispatch,
            notConfidentSubmissions,
            processStudentFeedback,
            checkStudentFeedbackFilter,
            checkPatternFilters,
            checkMissingPatternFeedback,
            checkUnitTestFilters,
            checkSearchParam,
            checkFlakyFilter,
            checkReviewFilter,
        ],
    )

    // Main clusters computation with memoization
    const clustersResult = useMemo(() => {
        if (!applyFilter) {
            return { clusters: rawClusters || [], fileIdsToSelect: [] }
        }

        // Override deferred criteriaFilteredFileIds with immediate value to prevent stale data
        const filtersWithImmediateCriteria = {
            ...deferredFilters,
            criteriaFilteredFileIds,
            hasCriteriaToFilter,
        }

        return processCluster(rawClusters, filtersWithImmediateCriteria)
    }, [rawClusters, deferredFilters, processCluster, applyFilter, criteriaFilteredFileIds, hasCriteriaToFilter])

    return {
        clusters: clustersResult.clusters,
        fileIdsToSelect: clustersResult.fileIdsToSelect,
        isFetching: false, // Add actual loading states as needed
        isPendingTransition: !isEqual(clusterFilters, deferredFilters),
    }
}

export default useClusterGroups
